import React from "react";
import PropTypes from "prop-types";
import {
  Card,
  Box,
  Typography,
  Stack,
  InputAdornment,
  IconButton,
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import {
  FileText,
  ShieldCheck,
  Scale,
  Sparkles,
} from "lucide-react";
import { colors, borderRadiusPx, customShadows } from "../../../../../theme";
import { CommonInput, CommonButton } from "../../../../Components/ui";

const TermsHero = ({
  searchQuery = "",
  onSearchChange,
  onScrollToSection,
}) => {
  return (
    <Card
      sx={{
        p: { xs: 3, sm: 4 },
        borderRadius: borderRadiusPx.xl,
        backgroundColor: colors.dashboard.cardBg,
        border: `1px solid ${colors.dashboard.cardBorder}`,
        boxShadow: customShadows.cardFlat,
        position: "relative",
        overflow: "hidden",
        mb: 4,
      }}
    >
      {/* Top Accent Gradient Stripe */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: colors.gradients.primary,
        }}
      />

      <Stack spacing={2.5}>
        {/* Title & Badge */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: borderRadiusPx.lg,
              bgcolor: colors.blue[50],
              color: colors.primary.main,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 2px 8px rgba(37, 99, 235, 0.15)",
            }}
          >
            <FileText size={24} />
          </Box>
          <Box>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 900,
                color: colors.slate[900],
                letterSpacing: "-0.5px",
                fontSize: { xs: "1.75rem", sm: "2.25rem", md: "2.5rem" },
                lineHeight: 1.15,
              }}
            >
              Terms and Conditions
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: colors.primary.main,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              Legal Guidelines & Platform Rules
            </Typography>
          </Box>
        </Box>

        <Typography
          variant="body1"
          sx={{
            color: colors.slate[600],
            fontSize: { xs: "0.925rem", sm: "1rem" },
            maxWidth: 820,
            lineHeight: 1.65,
          }}
        >
          Please read these Terms and Conditions carefully before using the AutoBET Lottery ecosystem.
          These terms govern all user participation, creator lottery deployments, gift cards, and platform interactions.
        </Typography>

        {/* Interactive Search Filter */}
        <Box sx={{ maxWidth: 600, width: "100%" }}>
          <CommonInput
            fullWidth
            size="small"
            placeholder="Search clauses, keywords (e.g. registration, fees, VRF, creators)..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            startAdornment={
              <InputAdornment position="start">
                <SearchIcon sx={{ color: colors.slate[400], fontSize: 22 }} />
              </InputAdornment>
            }
            endAdornment={
              searchQuery ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => onSearchChange("")} sx={{ p: 0.25 }}>
                    <ClearIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ) : null
            }
            sx={{
              bgcolor: colors.background.paper,
              boxShadow: "0 2px 10px rgba(0, 0, 0, 0.04)",
            }}
          />
        </Box>

        {/* Quick Navigation Anchor Buttons */}
        <Stack direction="row" flexWrap="wrap" sx={{ gap: 1.25 }}>
          <CommonButton
            variant="outline"
            size="small"
            onClick={() => onScrollToSection("section-1")}
            startIcon={<Scale size={16} />}
          >
            1. Acceptance of Terms
          </CommonButton>
          <CommonButton
            variant="outline"
            size="small"
            onClick={() => onScrollToSection("section-4")}
            startIcon={<Sparkles size={16} />}
          >
            4. Creator Rules & Fees
          </CommonButton>
          <CommonButton
            variant="outline"
            size="small"
            onClick={() => onScrollToSection("section-11")}
            startIcon={<ShieldCheck size={16} />}
          >
            11. Disclaimers & Liability
          </CommonButton>
        </Stack>
      </Stack>
    </Card>
  );
};

TermsHero.propTypes = {
  searchQuery: PropTypes.string,
  onSearchChange: PropTypes.func.isRequired,
  onScrollToSection: PropTypes.func.isRequired,
};

export default TermsHero;
